import { DynamicPropertyDefinition, DynamicPropertyEntry } from '../shared/types';

// Type alias for compatibility with FlexiblePropertiesForm
type FlexiblePropertyEntry = { id: number; propertyKey: string; value: string | number };

/**
 * Build initial dynamic form entries from the animal metadata.
 * Each key in the metadata becomes an entry.
 * If a metadata value is null or undefined, it is replaced with an empty string.
 */
export const buildInitialMetadata = (metadata: Record<string, any>): DynamicPropertyEntry[] => {
  if (!metadata) {
    return [];
  }

  const entries: DynamicPropertyEntry[] = [];

  Object.keys(metadata).forEach((key) => {
    const value = metadata[key];
    // Skip the entry if the value is null or undefined.
    if (value == null) {
      return;
    }

    entries.push({
      id: Date.now() + Math.floor(Math.random() * 1000),
      propertyKey: key,
      value
    });
  });

  return entries;
};
/**
 * Build a new metadata object from the current dynamic form entries.
 * This replaces the original metadata completely with only the current entries.
 * For numeric fields, values are converted to numbers if they were originally numbers.
 */
export const buildMetadataFromEntries = (
  initialMetadata: Record<string, any>,
  entries: DynamicPropertyEntry[] | FlexiblePropertyEntry[]
): Record<string, any> => {
  // Start with an empty object - only include properties that are in current entries
  const result: any = {};

  // Build metadata only from current entries
  entries.forEach((entry) => {
    if (entry.propertyKey && entry.propertyKey.trim()) {
      // Check if the key existed in original metadata to preserve type
      const originalValue = initialMetadata ? initialMetadata[entry.propertyKey] : null;
      if (typeof originalValue === 'number') {
        const parsedNumber = Number(entry.value);
        result[entry.propertyKey] = isNaN(parsedNumber) ? 0 : parsedNumber;
      } else {
        result[entry.propertyKey] = entry.value;
      }
    }
  });

  return result;
};

/**
 * Build the dynamic property definitions based on the provided metadata.
 * If metadata is null or undefined, the function falls back to a default instance,
 * so that all available keys defined in the interface are returned.
 */
export const getAvailableProperties = (metadata: Record<string, any>): DynamicPropertyDefinition[] => {
  const objToReflect = metadata || {};
  const properties: DynamicPropertyDefinition[] = [];

  Object.keys(objToReflect).forEach((key) => {
    const value = objToReflect[key];
    const type: 'number' | 'string' = typeof value === 'number' ? 'number' : 'string';
    properties.push({
      key,
      // The label is the translation key (e.g. "f-animal-previouslyKnownBirthCycles").
      label: `f-animal-${key}`,
      type
    });
  });

  return properties;
};
